import { EventEmitter, Injectable, inject } from '@angular/core';

import { UserService, WINDOW } from '@frontend/vanilla/core';

import { GeoCoordinatorClientConfig } from './client-config';
import {
    ConfigurationData,
    CoordinatorStates,
    ICoordinatorProbeResult,
    IErrorData,
    IInitializationInput,
    IIntegrationApp,
    OnCoordinatorProbed,
    OnError,
    OnInitialized,
    OnUninitialized,
    Optional,
} from './client-models/coordinator.models';
import { LocationInfoStatus, LocationInfoStatusOptions } from './client-models/location-info';
import { PermissionInfo, PermissionsManager, PermissionsManagerable } from './permissions-manager/permissions';

@Injectable({
    providedIn: 'root',
})
export class GeoCoordinatorService implements IIntegrationApp, PermissionsManagerable {
    private _onInitialized: OnInitialized<CoordinatorStates>;
    private _onUninitialized: OnUninitialized;
    private _onError: OnError;
    private _onProbed: Optional<OnCoordinatorProbed>;
    private geolocationCoordinator: Optional<IIntegrationApp & PermissionsManagerable> = undefined;
    private readonly geolocationConfig = inject(GeoCoordinatorClientConfig);
    private _initializePending = false;
    private _uninitializePending = false;
    private _initializationInput: IInitializationInput | null | undefined;
    isSynchronizedUninit = false;
    private readonly windowRef = inject(WINDOW);
    private readonly userService = inject(UserService);

    constructor() {
        this.hookupCoordinator();
        addEventListener(
            'geolocationcoordinator:bootstrapped',
            () => {
                this.hookupCoordinator();
            },
            {
                once: true,
            },
        );
    }
    readLocationInfoStatus(): Optional<LocationInfoStatus> {
        return this.geolocationCoordinator?.readLocationInfoStatus();
    }

    get configuration(): Optional<ConfigurationData> {
        return this.geolocationCoordinator?.configuration;
    }

    triggerFetchLocation(input?: IInitializationInput): void {
        this.geolocationCoordinator?.triggerFetchLocation(input);
    }

    onAvailable: EventEmitter<GeoCoordinatorService> = new EventEmitter<GeoCoordinatorService>();

    get permissionsManager(): Optional<PermissionsManager> {
        return this.geolocationCoordinator?.permissionsManager;
    }

    get appDownloadUrl(): string {
        return this.geolocationCoordinator?.appDownloadUrl || '';
    }

    probe(): void {
        this.geolocationCoordinator?.probe();
    }

    initialize(input?: IInitializationInput): void {
        this.isSynchronizedUninit = false;
        if (this.geolocationCoordinator) {
            this.geolocationCoordinator.initialize({
                session: (input && input.session) || (this.userService && this.userService.ssoToken) || 'NotAvailable',
                channelId: input && input.channelId,
                plcIdentifier: input && input?.plcIdentifier,
                showPLCPopup: input && input.showPLCPopup,
            });
        } else {
            this._initializePending = true;
            this._initializationInput = input;
        }
    }

    uninitialize(): void {
        if (this.geolocationCoordinator) {
            this.geolocationCoordinator.uninitialize();
        } else {
            this._uninitializePending = true;
        }
    }

    locationInfoStatus(infoData: LocationInfoStatusOptions): void {
        this.geolocationCoordinator?.locationInfoStatus(infoData);
    }
    handleLocationInfoStatus(
        locationInfoStatus?: LocationInfoStatus,
        notOkCallback?: (permissionsInfo: PermissionInfo) => void,
        okCallback?: () => void,
    ): void {
        this.geolocationCoordinator?.handleLocationInfoStatus(locationInfoStatus, notOkCallback, okCallback);
    }

    set onInitialized(callback_function: OnInitialized<CoordinatorStates>) {
        this._onInitialized = callback_function;
        if (this.geolocationCoordinator) {
            this.geolocationCoordinator.onInitialized = this._onInitialized;
        }
    }

    set onUninitialized(value: OnUninitialized) {
        this._onUninitialized = value;
        if (this.geolocationCoordinator) {
            this.geolocationCoordinator.onUninitialized = value;
        }
    }

    set onError(value: OnError) {
        this._onError = value;
        if (this.geolocationCoordinator) {
            this.geolocationCoordinator.onError = value;
        }
    }

    set onProbed(value: OnCoordinatorProbed) {
        this._onProbed = value;
        if (this.geolocationCoordinator) {
            this.geolocationCoordinator.onProbed = value;
        }
    }

    registerUnload() {
        this.windowRef.addEventListener('unload', () => {
            //console.info('Window onunload::');
            this.uninitialize();
        });
        this.windowRef.removeEventListener('message', this.coordinationSynchronizer);
    }

    registerSynchronization() {
        const geolocationConfig = this.geolocationConfig;
        if (geolocationConfig.isEnabled && geolocationConfig.synchronizationSettings.enabled) {
            this.windowRef.addEventListener('message', this.coordinationSynchronizer);
        }
    }

    private onGeolocationError(errorData: IErrorData): void {
        if (this.onError) {
            this.onError(errorData);
        } else {
            if (this._onError) {
                this._onError(errorData);
            }
        }
    }

    private onGeolocationInitialized(initializationData: Optional<CoordinatorStates>, locationInfoStatus?: LocationInfoStatus): void {
        if (this.onInitialized) {
            this.onInitialized(initializationData, locationInfoStatus);
        } else {
            if (this._onInitialized) {
                this._onInitialized(initializationData, locationInfoStatus);
            }
        }
    }

    private onGeolocationUninitialized(): void {
        if (this.onUninitialized) {
            this.onUninitialized();
        } else {
            if (this._onUninitialized) {
                this._onUninitialized();
            }
        }
    }

    private onGeolocationProbed(probeResult: ICoordinatorProbeResult): void {
        if (this.onProbed) {
            this.onProbed(probeResult);
        } else {
            if (this._onProbed) {
                this._onProbed(probeResult);
            }
        }
    }

    private hookupCoordinator(): void {
        const geolocationCoordinator = (this.windowRef as any).geolocationCoordinator as IIntegrationApp & PermissionsManagerable;
        if (geolocationCoordinator) {
            this.geolocationCoordinator = geolocationCoordinator;
            this.geolocationCoordinator.onError = this.onGeolocationError.bind(this);
            this.geolocationCoordinator.onInitialized = this.onGeolocationInitialized.bind(this);
            this.geolocationCoordinator.onUninitialized = this.onGeolocationUninitialized.bind(this);
            this.geolocationCoordinator.onProbed = this.onGeolocationProbed.bind(this);
            if (this.onAvailable) {
                this.onAvailable.emit(this);
            }

            if (this._initializePending) {
                this._initializePending = false;
                this.initialize(this._initializationInput || undefined);
                this._initializationInput = null;
                return;
            }
            if (this._uninitializePending) {
                this._uninitializePending = false;
                this.uninitialize();
            }
        }
    }

    private readonly coordinationSynchronizer = (messageEvent: MessageEvent): void => {
        const geolocationConfig = this.geolocationConfig;
        if (
            geolocationConfig.isEnabled &&
            geolocationConfig.synchronizationSettings.enabled &&
            messageEvent &&
            messageEvent.data &&
            messageEvent.data.trim &&
            geolocationConfig.synchronizationSettings.events.stop.includes(messageEvent.data.trim())
        ) {
            this.isSynchronizedUninit = true;
            this.geolocationCoordinator?.uninitialize();
        }
    };
}
